// Tab "Materiaal": camera, grip, licht — los van props, met huurperiode en "retour"-vinkje.
import React, { useState } from 'react';
import { Box, Checkbox, IconButton, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { bevestig } from '../../../utils/bevestig';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import { meldFout } from '../../../utils/apiFout';
import { api, korteDatum, nieuwMateriaal, vandaagIso, wijzigMateriaal, INKT, STIL } from '../breakdownApi';
import { Kaart, Leeg, Primair, Rij, Uitleg } from '../onderdelen';

const LEEG = { name: '', vendor: '', pickup_date: '', return_date: '', notes: '' };

export default function MateriaalTab({ productie, materiaal, setMateriaal }) {
  const [form, setForm] = useState(LEEG);
  const vervang = (m) => setMateriaal((lijst) => lijst.map((x) => (x.id === m.id ? m : x)));

  async function voegToe() {
    if (!form.name.trim()) return;
    try {
      const m = await nieuwMateriaal(productie.id, { ...form, pickup_date: form.pickup_date || null, return_date: form.return_date || null });
      setMateriaal([...materiaal, m]);
      setForm(LEEG);
    } catch (e) { meldFout(e, 'Materiaal toevoegen'); }
  }

  async function retour(m) {
    const vorige = m.is_returned;
    try {
      vervang(await wijzigMateriaal(m.id, { is_returned: !vorige }));
      biedOngedaanAan(`${m.name} ${vorige ? 'niet meer retour' : 'retour'}`, async () => vervang(await wijzigMateriaal(m.id, { is_returned: vorige })));
    } catch (e) { meldFout(e, 'Materiaal opslaan'); }
  }

  async function verwijder(m) {
    const ok = await bevestig({ titel: `${m.name} verwijderen?`, tekst: 'Dit materiaal gaat van de lijst af.', knop: 'Verwijderen', gevaarlijk: true });
    if (!ok) return;
    try {
      await api(`/equipment/${m.id}`, { method: 'DELETE', wat: 'Materiaal verwijderen' });
      setMateriaal((lijst) => lijst.filter((x) => x.id !== m.id));
    } catch (e) { meldFout(e, 'Materiaal verwijderen'); }
  }

  const nogTerug = materiaal.filter((m) => !m.is_returned).length;
  return (
    <Box>
      <Uitleg>Gehuurd materiaal met ophaal- en retourdatum. {materiaal.length ? `${nogTerug} van ${materiaal.length} nog niet retour.` : ''}</Uitleg>
      {materiaal.length === 0 ? <Leeg>Nog geen materiaal. Camera, grip en licht horen hier — props staan in de Breakdown.</Leeg> : (
        <Kaart>
          {materiaal.map((m) => {
            const telaat = m.return_date && !m.is_returned && m.return_date < vandaagIso();
            return (
              <Rij key={m.id}>
                <Checkbox size="small" checked={m.is_returned} onChange={() => retour(m)} inputProps={{ 'aria-label': `${m.name} retour` }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: m.is_returned ? STIL : INKT, textDecoration: m.is_returned ? 'line-through' : 'none' }}>{m.name}</Typography>
                  <Typography noWrap sx={{ fontSize: 12.5, color: STIL }}>{[m.vendor, m.notes].filter(Boolean).join(' · ') || '—'}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: telaat ? '#b91c1c' : STIL, fontWeight: telaat ? 700 : 400, whiteSpace: 'nowrap' }}>
                  {m.pickup_date || m.return_date ? `${korteDatum(m.pickup_date) || '…'} → ${korteDatum(m.return_date) || '…'}` : ''}
                </Typography>
                <IconButton size="small" aria-label={`${m.name} verwijderen`} onClick={() => verwijder(m)}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Rij>
            );
          })}
        </Kaart>
      )}
      <Kaart sx={{ mt: 2, p: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr 150px 150px' }, gap: 1, alignItems: 'center' }}>
        <TextField size="small" label="Wat" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') voegToe(); }} />
        <TextField size="small" label="Verhuurder" value={form.vendor} onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))} />
        <TextField size="small" type="date" label="Ophalen" InputLabelProps={{ shrink: true }} value={form.pickup_date} onChange={(e) => setForm((f) => ({ ...f, pickup_date: e.target.value }))} />
        <TextField size="small" type="date" label="Retour" InputLabelProps={{ shrink: true }} value={form.return_date} onChange={(e) => setForm((f) => ({ ...f, return_date: e.target.value }))} />
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 1 }}>
          <TextField size="small" fullWidth label="Notitie" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          <Primair onClick={voegToe} disabled={!form.name.trim()}>Toevoegen</Primair>
        </Box>
      </Kaart>
    </Box>
  );
}
